import { TriangleAlert } from '@myosh/icons';
import { Badge, Card, CardAction, CardDescription, CardHeader, CardTitle, cn, Skeleton } from '@myosh/odin-components';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { type ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useCountProjectedRecordsForViewQuery, useDetailedCountForViewQuery } from '@/api/enhanced/schedule-view-api';
import { summarizeDetailedCount, summarizeProjection } from '@/utils/schedule-counts';

type Tone = 'error' | 'warning' | 'success' | 'info';

const toneStyles: Record<Tone, string> = {
  error: 'bg-error',
  warning: 'bg-warning',
  success: 'bg-success',
  info: 'bg-info',
};

const StatCard = ({
  label,
  value,
  tone,
  isLoading,
  action,
}: {
  label: string;
  value: number | undefined;
  tone: Tone;
  isLoading: boolean;
  action?: ReactNode;
}) => {
  const { t } = useTranslation();
  const labelId = useId();

  return (
    <Card role="group" aria-labelledby={labelId}>
      <CardHeader>
        <CardDescription id={labelId} className="flex items-center gap-2">
          <span aria-hidden="true" className={cn('size-2 shrink-0 rounded-full', toneStyles[tone])} />
          {label}
        </CardDescription>
        <CardTitle className="font-semibold text-2xl tabular-nums">
          {isLoading && <Skeleton className="h-8 w-12" />}
          {!isLoading &&
            (value ?? (
              <span className="font-normal text-muted-foreground text-sm">{t(($) => $.countUnavailable)}</span>
            ))}
        </CardTitle>
        {action && <CardAction>{action}</CardAction>}
      </CardHeader>
    </Card>
  );
};

// The cards count the whole view; the table below them shows one page of it.
export const ScheduleStats = ({ hashKey }: { hashKey: string }) => {
  const { t } = useTranslation();
  const detailed = useDetailedCountForViewQuery(hashKey ? { viewId: hashKey } : skipToken);
  const projected = useCountProjectedRecordsForViewQuery(hashKey ? { viewId: hashKey } : skipToken);

  const counts = detailed.isError ? undefined : summarizeDetailedCount(detailed.data?.result);
  const projection = projected.isError ? undefined : summarizeProjection(projected.data);
  const isProjectionStale = !projected.isFetching && projected.isSuccess && projection?.isStale;

  return (
    <section aria-label={t(($) => $.summary)} className="@container flex flex-col gap-2 p-2">
      <div className="grid @2xl:grid-cols-4 grid-cols-2 gap-2">
        <StatCard
          label={t(($) => $.statusOverdue)}
          value={counts?.overdue}
          tone="error"
          isLoading={detailed.isFetching}
        />
        <StatCard
          label={t(($) => $.statusInProgress)}
          value={counts?.progress}
          tone="warning"
          isLoading={detailed.isFetching}
        />
        <StatCard
          label={t(($) => $.statusCompleted)}
          value={counts?.completed}
          tone="success"
          isLoading={detailed.isFetching}
          action={!detailed.isFetching && counts && <Badge variant="outline">{counts.completedPercent}%</Badge>}
        />
        <StatCard
          label={t(($) => $.statusPlanned)}
          value={projection?.planned}
          tone="info"
          isLoading={projected.isFetching}
          action={
            isProjectionStale && (
              <Badge role="status" variant="outline" className="text-warning">
                <TriangleAlert />
                {t(($) => $.projectionOutOfDate)}
              </Badge>
            )
          }
        />
      </div>
      <p className="text-muted-foreground text-xs">{t(($) => $.countsCoverWholeView)}</p>
    </section>
  );
};
